import { ToastrService } from 'ngx-toastr';
import { Article, ArticleElementType, ArticlePreference, ArticlePreferenceType, ArticleAccessLevelType, ArticleStatus, ArticleLanguageType } from '../../../models/article';
import { Component, Input, inject, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ArticleService } from 'src/app/services/article.service';
import { LoaderService } from 'src/app/services/loader.service';
import { AuthService } from 'src/app/shared/auth/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { TranslationService } from '../../../services/translation.service';

@Component({
  selector: 'app-article-preview',
  templateUrl: './article-preview.component.html',
  styleUrls: ['./article-preview.component.scss']
})
export class ArticlePreviewComponent {

  @Input() articleId: number;
  @Input() article: Article;
  @Input() isRedirectedForArticleApproval: boolean;
  articleElementType = ArticleElementType;
  isMyArticle = 0;
  selectedPrefenceType: ArticlePreferenceType = ArticlePreferenceType.NoPreference;
  articlePrefence: ArticlePreference;
  articlePreferenceType = ArticlePreferenceType;
  clonePrefenceType: ArticlePreferenceType = ArticlePreferenceType.NoPreference;
  language: ArticleLanguageType = ArticleLanguageType.English;
  private destroy$ = new Subject<void>();

  constructor(
    private articleService: ArticleService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private authService: AuthService,
    private router: Router,
    public translationService: TranslationService
  ) {
    this.language = this.translationService.language == 'id' ? ArticleLanguageType.Indonesian : ArticleLanguageType.English;
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params["articleId"]) {
        this.articleId = params["articleId"];
      }

      if (params["isMyArticle"]) {
        this.isMyArticle = Number.parseInt(params["isMyArticle"]);
      }
    });
    if (this.articleId && !this.article) {
      this.loaderService.setLoading(true);
      this.articleService.getArticleByIdForPreview(this.articleId, this.language).subscribe((response: Article) => {
        let data = response;
        if (this.language == ArticleLanguageType.Indonesian) {
          data.articleBlocks = response.articleBlocks.map(item => ({
            ...item,
            data: item.translatedData
          }))
        }
        this.isMyArticle = (response.status == ArticleStatus.Review) ? 0 : this.isMyArticle;
        this.setArticleData(data);
        this.loaderService.setLoading(false);
      }, (err) => {
        if (this.isMyArticle) {
          this.router.navigate(['articles', 'my']);
        } else {
          this.router.navigate(['articles', 'liked']);
        }
        this.loaderService.setLoading(false);
      });
    } else if (this.article) {
      if (this.language == ArticleLanguageType.Indonesian) {
        this.article.articleBlocks = this.article.articleBlocks.map(item => ({
          ...item,
          data: item.translatedData
        }))
      }
      this.setArticleData(this.article);
    }

    // Subscribe to language change events
    this.translationService.changeLangEmitted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        const currentUrl = this.router.url;
        if (currentUrl.includes('article-preview')) {
          const segments = currentUrl.split('/');
          this.articleId = Number(segments[segments.length - 1]);
          this.language = lang == 'id' ? ArticleLanguageType.Indonesian : ArticleLanguageType.English;
          this.loaderService.setLoading(true);
          this.articleService.getArticleByIdForPreview(this.articleId, this.language).subscribe((response: Article) => {
            let data = response;
            if (this.language == ArticleLanguageType.Indonesian) {
              data.articleBlocks = response.articleBlocks.map(item => ({
                ...item,
                data: item.translatedData
              }))
            }
            this.isMyArticle = (response.status == ArticleStatus.Review) ? 0 : this.isMyArticle;
            this.setArticleData(data);
            this.loaderService.setLoading(false);
          }, (err) => {
            if (this.isMyArticle) {
              this.router.navigate(['articles', 'my']);
            } else {
              this.router.navigate(['articles', 'liked']);
            }
            this.loaderService.setLoading(false);
          });
        }
      });
  }

  setArticleData(article: Article) {
    this.article = article;
    this.article?.articleBlocks.forEach(x => {
      if (x.elementId === ArticleElementType.Image) {
        const carouselData = JSON.parse(x.data);
        x.imageCount = carouselData.Images.filter(x => x).length;
      }
    });
    this.articlePrefence = this.article?.articlePreference;
    this.selectedPrefenceType = this.articlePrefence?.preference;
    if (this.article.accessLevel == ArticleAccessLevelType.Public) {
      this.article.accessLevelStyle = 'theme-pink';
    } else if (this.article.accessLevel == ArticleAccessLevelType.Connections) {
      this.article.accessLevelStyle = 'theme-green';
    } else if (this.article.accessLevel = ArticleAccessLevelType.Followers) {
      this.article.accessLevelStyle = 'theme-yellow';
    }
  }

  onChange(e: any) {
    this.clonePrefenceType = this.selectedPrefenceType ?? ArticlePreferenceType.NoPreference;
    this.selectedPrefenceType = this.selectedPrefenceType == e ? ArticlePreferenceType.NoPreference : e;

    if (this.articlePrefence?.id) {
      this.articlePrefence.preference = this.selectedPrefenceType;
    } else {
      this.articlePrefence = {
        articleId: this.article.id,
        preference: this.selectedPrefenceType,
      } as ArticlePreference;
    }
    this.articleService.upsertPrefence(this.articlePrefence).subscribe((res: ArticlePreference) => {
      if (res) {
        this.articlePrefence = res;
        this.article.commentCount = this.articlePrefence.commentCount;
        this.article.likeCount = this.articlePrefence.likeCount;
        this.article.dislikeCount = this.articlePrefence.dislikeCount;
      }
    }, err => {
      this.selectedPrefenceType = this.clonePrefenceType;
      this.authService.updateReturnURL(this.router.url);
    });
  }

  removeArticle(articleId: number) {
    this.loaderService.setLoading(true);
    this.articleService.delete('/api/article', articleId).subscribe(res => {
      this.router.navigate(['articles', 'my']);
      this.loaderService.setLoading(false);
    }, err => {
      this.loaderService.setLoading(false);
      this.router.navigate(['articles', 'my']);
    });
  }


  private modalService = inject(NgbModal);
  openDeleteSectionModal(deleteSectionModal: TemplateRef<any>, articleId: number) {

    if (this.article.status === ArticleStatus.Published) {
      this.toastr.error("You can't delete this article beacause it is already published!");
      return;
    }

    const modalRef = this.modalService.open(deleteSectionModal, {
      windowClass: 'delete-section-modal',
      centered: true,
      size: 'sm',
    });

    modalRef.result.then(
      (result) => {
        if (result) {
          // Execute the remove function or any other logic here
          this.removeArticle(articleId);
        }
      },
      (reason) => {
        // Handle modal dismissal or other reasons
      }
    );
  }
}
